import React, { useMemo } from "react";

import { Route, Routes, Navigate, useSearchParams } from "react-router";
import { getHomeRoutes } from "@companion-core/shared/app/Utils/config";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";

// Components
import { DynamicPage } from "@companion-core/shared/app/Components/DynamicPage/DynamicPage";
import { DynamicSection } from "@companion-core/shared/app/Components/MyAccount/DynamicSection";
import { NeedHelp } from "@companion-core/shared/app/Components/NeedHelp";
import { DetailsPage } from "@companion-core/shared/app/Components/DetailsPages";
import { CrossSell } from "@companion-core/shared/app/Components/CrossSell";
import { SSOPartnerConsent } from "@companion-core/shared/app/Components/SSOPartnerConsent";
import AuthGuard from "@companion-core/web/src/Navigation/Guards/AuthGuard";
import OnboardingGuard from "@companion-core/web/src/Navigation/Guards/OnboardingGuard";
import RootRedirect from "@companion-core/web/src/Navigation/Guards/RootRedirect";
import SSOGuard from "@companion-core/web/src/Navigation/Guards/SSOGuard";
import Login from "@companion-core/web/src/Screens/Login";
import RecoverPassword from "@companion-core/web/src/Screens/RecoverPassword";
import ResetPassword from "@companion-core/web/src/Screens/ResetPassword";
import { Register, Activation } from "@companion-core/shared/app/Components/SignUp";
import Requests from "@companion-core/web/src/Screens/Requests/Requests";
import Detail from "@companion-core/web/src/Screens/Requests/Detail";
import Editor from "@companion-core/web/src/Screens/Requests/Editor";
import MyAccount from "@companion-core/web/src/Screens/MyAccount/MyAccount";
import SSOClient from "@companion-core/web/src/Screens/SSO/SSOClient";
import {
  SSORegister,
  SSORegisterLoading,
  AdditionalVerification,
  PostSSORegisterFailure,
  CodeValidation,
} from "@companion-core/web/src/Screens/SSO/Register";
import LinkCodeClient from "@companion-core/web/src/Screens/LinkCode/LinkCodeClient";
import { NotFound } from "@companion-core/web/src/Screens/Errors/NotFound";
import { ServerError } from "@companion-core/web/src/Screens/Errors/ServerError";
import { NoRights } from "@companion-core/web/src/Screens/Errors/SSO/NoRights";
import { TechnicalError } from "@companion-core/web/src/Screens/Errors/SSO/TechnicalError";
import { ConfigGuard } from "./Guards/ConfigGuard";
import {
  hasCodeQueryParam,
  hasLinkCodeQueryParam,
} from "@companion-core/web/src/Utilities/navigation";
import { RequestRecommendationDetail } from "@companion-core/shared/app/Components/Requests/RequestRecommendationDetail";
import { RecommendedList } from "@companion-core/shared/app/Components/RecommendedList/RecommendedList";
import { OnboardingBellConfirmation } from "@companion-core/shared/app/Components/Bell/Confirmation/Onboarding";

const Navigation = () => {
  const authenticated = useAuthStore.getState().authenticated;
  const { logged: homeRouteLogged, unlogged: homeRouteUnlogged } = getHomeRoutes();
  const homePage = authenticated ? homeRouteLogged : homeRouteUnlogged;
  const [searchParams] = useSearchParams();
  const isSSOCompanionWithCodeParam = useMemo(
    () => hasCodeQueryParam(searchParams),
    [searchParams]
  );
  const hasLinkCodeQuery = useMemo(() => hasLinkCodeQueryParam(searchParams), [searchParams]);

  return (
    <Routes>
      <Route element={<ConfigGuard />}>
        <Route
          element={
            isSSOCompanionWithCodeParam ? (
              <SSOClient />
            ) : hasLinkCodeQuery ? (
              <LinkCodeClient />
            ) : undefined
          }>
          <Route path="/need-help" element={<NeedHelp />} />
          <Route path="/" element={<RootRedirect homePage={homePage} />} />
          <Route element={<AuthGuard />}>
            <Route element={<OnboardingGuard />}>
              <Route
                path="/:origin/content-type/:contentType/details/:id"
                element={<DetailsPage />}
              />
              <Route path="/:origin/details/:id" element={<DetailsPage />} />
              <Route id="" path="/:slug" element={<DynamicPage />} />
              <Route path="/authorize" element={<SSOPartnerConsent />} />
              <Route path="/my-account" element={<MyAccount />}>
                <Route path=":section" element={<DynamicSection />} />
              </Route>
              <Route path="/my-requests/:route?" element={<Requests />} />
              <Route path="/my-requests/:id/details/:content/:contentId?" element={<Detail />} />
              <Route path="/my-requests/:id/editor/:action" element={<Editor />} />
              <Route
                path="/my-requests/:id/proposals/:proposalId/recommendations/:recommendationId"
                element={<RequestRecommendationDetail />}
              />
              <Route path="/my-requests/cross-sell/:id/:subId?" element={<CrossSell />} />
              <Route path="/recommended-list" element={<RecommendedList />} />
              <Route path="/request-confirmation" element={<OnboardingBellConfirmation />} />

              {/*
                Temporary route to test the BNPP register screens, only available in Dev and Staging
                TODO : Remove this route after the new register flow is deployed
              */}
              {(import.meta.env.VITE_ENVIRONMENT === "development" ||
                import.meta.env.VITE_ENVIRONMENT === "staging") && (
                <Route path="/post-sso-register">
                  <Route path="loading" element={<SSORegisterLoading />} />
                  <Route path="failure" element={<PostSSORegisterFailure />} />
                  <Route path="additional-verification" element={<AdditionalVerification />} />
                  <Route path="activation" element={<SSORegister />} />
                  <Route path="code-validation" element={<CodeValidation />} />
                </Route>
              )}
            </Route>
          </Route>
        </Route>

        <Route element={<AuthGuard publicOnly />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register">
            <Route index element={<Navigate to="/register/member" />} />
            <Route path="member" element={<Register />} />
            <Route path="activation" element={<Activation />} />
          </Route>
          <Route path="/password">
            <Route index element={<Navigate to="/password/recover" />} />
            <Route path="recover" element={<RecoverPassword />} />
            <Route path="reset">
              <Route path=":token" element={<ResetPassword />} />
            </Route>
          </Route>
        </Route>

        <Route element={<SSOGuard onlyAvailableForSSOCompanion />}>
          <Route path="/sso">
            <Route path=":token" element={<SSOClient />} />
            <Route index path="no-rights" element={<NoRights />} />
            <Route path="error" element={<TechnicalError />} />
          </Route>
        </Route>
        <Route path="/not-found" element={<NotFound />} />
        <Route path="/error" element={<ServerError />} />
        <Route path="*" element={<Navigate to="/not-found" />} />
      </Route>
    </Routes>
  );
};

export default Navigation;
